import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api/contexto'
import type { ErrorApi } from '../api/errores'
import type { ClienteDetalle } from '../paginas/logistica/consultas'
import type { DatosCliente, PaginaClientes, PrecioCliente } from '../api/modelos'
import { TAMANO_PAGINA } from '../componentes/paginacion'

/**
 * Pantalla Clientes (RF-40): lista con búsqueda y paginada. Con `incluirBajas`
 * pide también los de baja (`incluir_bajas=true`), para poder reactivarlos.
 */
export function useListaClientes(q: string, page: number, tamano = TAMANO_PAGINA, incluirBajas = false) {
  const api = useApi()
  return useQuery<PaginaClientes, ErrorApi>({
    queryKey: ['clientes', 'gestion', q, page, tamano, incluirBajas],
    queryFn: ({ signal }) =>
      api.pedir<PaginaClientes>('/api/clientes', {
        parametros: { q, page, page_size: tamano, ...(incluirBajas ? { incluir_bajas: 'true' } : {}) },
        senal: signal,
      }),
    placeholderData: keepPreviousData,
  })
}

function useRefrescarClientes() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: ['clientes'] })
    void qc.invalidateQueries({ queryKey: ['cliente'] })
  }
}

export function useCrearCliente() {
  const api = useApi()
  const refrescar = useRefrescarClientes()
  return useMutation<ClienteDetalle, ErrorApi, DatosCliente>({
    mutationFn: (datos) => api.pedir<ClienteDetalle>('/api/clientes', { metodo: 'POST', cuerpo: datos }),
    onSuccess: refrescar,
  })
}

export function useEditarCliente(id: string) {
  const api = useApi()
  const refrescar = useRefrescarClientes()
  return useMutation<ClienteDetalle, ErrorApi, DatosCliente>({
    mutationFn: (datos) => api.pedir<ClienteDetalle>(`/api/clientes/${id}`, { metodo: 'PATCH', cuerpo: datos }),
    onSuccess: refrescar,
  })
}

/** Precios pactados del cliente (RF-42). */
export function usePreciosCliente(id: string) {
  const api = useApi()
  return useQuery<{ items: PrecioCliente[]; total: number }, ErrorApi>({
    queryKey: ['precios-cliente', id],
    queryFn: ({ signal }) => api.pedir(`/api/clientes/${id}/precios`, { senal: signal }),
  })
}

export function useFijarPrecio(id: string) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { productId: string; precio: number }>({
    mutationFn: ({ productId, precio }) =>
      api.pedir(`/api/clientes/${id}/precios/${productId}`, { metodo: 'PUT', cuerpo: { precio } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['precios-cliente', id] }),
  })
}

export function useQuitarPrecio(id: string) {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, { productId: string }>({
    mutationFn: ({ productId }) => api.pedir(`/api/clientes/${id}/precios/${productId}`, { metodo: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['precios-cliente', id] }),
  })
}
